import React, { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { FloatingMenu, MENU_ITEM } from "../../components/ui/FloatingMenu";
import { ArrowUpRight, BellRing, Check, ExternalLink, ListPlus, MapPin, Plus, StickyNote, Store, UserRound, Users, X } from "lucide-react";
import { AddToListPicker } from "../../components/prospection/AddToListPicker";
import { Portfolio, ProspectSignal, ScoreFactor } from "../../interfaces/linkedinProspecting";
import { FOLLOW_UP_PRESETS, followUpDate, relativeFr } from "./vocabulary";

/**
 * The score as a green ring filled to its value, number in the middle. Hovering it lists the
 * points it is made of — the score is only worth trusting if it can be read.
 */
export function ScoreCircle({ score, factors }: { score: number; factors?: ScoreFactor[] }) {
    const size = 40;
    const stroke = 4;
    const r = (size - stroke) / 2;
    const circumference = 2 * Math.PI * r;
    return (
        <div className="group/score relative shrink-0">
            <div className="relative flex h-10 w-10 items-center justify-center" aria-label={`Score ${score} sur 100`}>
                <svg width={size} height={size} className="absolute inset-0 -rotate-90">
                    <circle cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} className="stroke-surface-sunken" />
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={r}
                        fill="none"
                        strokeWidth={stroke}
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={circumference * (1 - Math.min(100, Math.max(0, score)) / 100)}
                        className="stroke-success transition-all duration-150"
                    />
                </svg>
                <span className="relative font-sans text-xs font-semibold text-ink">{score}</span>
            </div>
            {factors && factors.length > 0 && (
                <div className="pointer-events-none absolute right-0 top-full z-20 mt-2 hidden w-64 rounded-2xl border border-border bg-surface p-3 shadow-float group-hover/score:block">
                    <p className="mb-1.5 font-sans text-xs font-semibold uppercase tracking-wide text-ink-faint">Score {score} / 100</p>
                    <ul className="space-y-1">
                        {factors.map((f) => (
                            <li key={f.label} className="flex items-start justify-between gap-3 font-sans text-xs text-ink">
                                <span>{f.label}</span>
                                <span className={`shrink-0 font-mono ${f.points < 0 ? "text-danger" : "text-ink-muted"}`}>
                                    {f.points > 0 ? "+" : ""}
                                    {f.points}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

/**
 * Age, portfolio size and zone on one line, each behind its own icon so the eye finds them
 * without labels. Missing facts are left out rather than shown as dashes.
 */
export function ProspectMeta({
    age, portfolio, external, name,
}: {
    age: number | null;
    portfolio: Portfolio;
    external?: boolean;
    /** When given, the establishment count links to the app's page listing everything this person runs. */
    name?: string;
}) {
    if (external) {
        return (
            <div className="inline-flex items-center gap-1 font-sans text-xs text-ink-muted">
                <ExternalLink className="h-3.5 w-3.5 text-ink-faint" />
                Invité hors page · pas dans la base
            </div>
        );
    }
    const { sites, cities } = portfolio;
    const shown = cities.slice(0, 2).join(", ");
    const more = cities.length - 2;
    const items: ReactNode[] = [];
    if (age) {
        items.push(
            <span key="age" className="inline-flex items-center gap-1">
                <UserRound className="h-3.5 w-3.5 text-ink-faint" />
                {age} ans
            </span>
        );
    }
    const sitesLabel = (
        <>
            <Store className="h-3.5 w-3.5 text-ink-faint" />
            {sites} établissement{sites > 1 ? "s" : ""}
        </>
    );
    items.push(
        name ? (
            <a
                key="sites"
                href={`/signals/founder?founderName=${encodeURIComponent(name)}`}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                title="Voir tous ses établissements"
                className="inline-flex items-center gap-1 rounded underline decoration-border-strong underline-offset-2 transition-colors hover:text-ink hover:decoration-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            >
                {sitesLabel}
            </a>
        ) : (
            <span key="sites" className="inline-flex items-center gap-1">
                {sitesLabel}
            </span>
        )
    );
    if (shown) {
        items.push(
            <span key="zone" className="inline-flex min-w-0 items-center gap-1" title={cities.join(", ")}>
                <MapPin className="h-3.5 w-3.5 shrink-0 text-ink-faint" />
                <span className="truncate">{shown}</span>
                {more > 0 && <span className="shrink-0 text-ink-faint">+{more}</span>}
            </span>
        );
    }
    return <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-0.5 font-sans text-xs text-ink-muted">{items}</div>;
}

/** Within this many days (~3 months) a signal is "live": it gets the blinking red dot. */
const LIVE_SIGNAL_DAYS = 90;

/**
 * A signal as plain text with a dot. Openings, new managers and events from the last 3 months get a blinking red "live" dot — the same
 * ping as the Database's "Données en temps réel" marker — so a recent opening jumps out of the
 * list; older ones keep a still dot.
 */
export function SignalTag({ signal }: { signal: ProspectSignal }) {
    const live = Date.now() - new Date(signal.date).getTime() <= LIVE_SIGNAL_DAYS * 86_400_000;
    const dot = signal.tone === "hot" ? "bg-accent" : signal.tone === "job" ? "bg-info" : "bg-ink-faint";
    return (
        <span className="inline-flex items-center gap-1.5 font-sans text-xs text-ink-muted" title={signal.establishmentName}>
            {live ? (
                <span className="relative flex h-2 w-2" aria-label="Récent">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-danger opacity-75 motion-safe:animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-danger" />
                </span>
            ) : (
                <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
            )}
            {signal.label} · {relativeFr(signal.date)}
        </span>
    );
}

export function initials(name: string): string {
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((w) => w[0]?.toUpperCase())
        .join("");
}

/**
 * Avatar tints, drawn from the design system's own hues (accent, warning, success, info, danger)
 * so the list stays in the page's warm palette: a soft two-stop gradient, initials in the deep tone.
 */
const AVATAR_TINTS = [
    { from: "#FDEBDD", to: "#F6C7A8", ink: "#B44A1E", ring: "#F1B48D" },
    { from: "#FCF2DF", to: "#F0D7A6", ink: "#8F5E14", ring: "#E8C88A" },
    { from: "#E6F4EC", to: "#BFE0CC", ink: "#246F47", ring: "#A8D3B9" },
    { from: "#E7EEF7", to: "#C3D5EA", ink: "#2D5784", ring: "#ABC4E0" },
    { from: "#FCE8E5", to: "#F2C1BA", ink: "#9E2C21", ring: "#EBAAA1" },
];

/** Same name, same tint — a person keeps their colour from one visit to the next. */
function tintFor(name: string) {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return AVATAR_TINTS[Math.abs(h) % AVATAR_TINTS.length];
}

export function Avatar({ name, size = "md" }: { name: string; size?: "sm" | "md" }) {
    const t = tintFor(name);
    return (
        <div
            className={`flex shrink-0 items-center justify-center rounded-full font-sans font-semibold tracking-wide ring-1 ring-inset ${
                size === "sm" ? "h-7 w-7 text-xs" : "h-10 w-10 text-[13px]"
            }`}
            style={{ backgroundImage: `linear-gradient(135deg, ${t.from} 0%, ${t.to} 100%)`, color: t.ink, ["--tw-ring-color" as string]: t.ring }}
            aria-hidden
        >
            {initials(name)}
        </div>
    );
}

/** A filter option as a small flat chip: ink when on, hairline when off. */
export function Chip({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={`rounded-full border px-2.5 py-0.5 font-sans text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent ${
                active ? "border-ink bg-ink text-ink-inverse" : "border-border text-ink-muted hover:border-border-strong hover:text-ink"
            }`}
        >
            {children}
        </button>
    );
}

/**
 * One discreet "+ Ajouter" link that opens a two-item menu — CRM or a prospection list — so a
 * row carries a single quiet entry point instead of two competing buttons. The list picker is
 * the app's own `AddToListPicker`, mounted around the menu so it outlives the menu closing.
 *
 * Once added, each item turns into its "Voir dans…" link, opening in a new tab — the CRM one
 * straight onto the prospect's sheet (`/kanban?openProspect=`), the list one onto the list.
 */
export function AddMenu({
    establishmentId, crmProspectId, onCrm, disabled,
}: {
    establishmentId: string | null;
    /** The rep's CRM prospect with the same LinkedIn profile, once there is one. */
    crmProspectId: string | null;
    onCrm: () => void;
    disabled?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const [list, setList] = useState<{ _id: string; name: string } | null>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    const close = useCallback(() => setOpen(false), []);

    const menu = (openListPicker?: (e: React.MouseEvent) => void) => (
        <div onClick={(e) => e.stopPropagation()}>
            <button
                ref={trigger}
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                title={crmProspectId ? "Voir dans mon CRM, ajouter à une liste" : "Ajouter au CRM ou à une liste"}
                aria-label="Ajouter"
                className={ICON_BUTTON}
            >
                <Plus className="h-4 w-4" />
            </button>
            <FloatingMenu anchor={trigger} open={open} onClose={close}>
                {crmProspectId ? (
                    <a role="menuitem" href={`/kanban?openProspect=${crmProspectId}`} target="_blank" rel="noopener noreferrer" onClick={close} className={MENU_ITEM}>
                        <Users className="h-4 w-4 text-success" />
                        <span className="flex-1">Voir dans mon CRM</span>
                        <ArrowUpRight className="h-3.5 w-3.5 text-ink-faint" />
                    </a>
                ) : (
                    <button
                        role="menuitem"
                        onClick={() => {
                            close();
                            onCrm();
                        }}
                        className={MENU_ITEM}
                    >
                        <Users className="h-4 w-4 text-ink-faint" />
                        Au CRM
                    </button>
                )}
                {list && (
                    <a role="menuitem" href={`/prospection/${list._id}`} target="_blank" rel="noopener noreferrer" onClick={close} title={list.name} className={MENU_ITEM}>
                        <ListPlus className="h-4 w-4 text-success" />
                        <span className="flex-1">Voir dans ma liste</span>
                        <ArrowUpRight className="h-3.5 w-3.5 text-ink-faint" />
                    </a>
                )}
                {openListPicker && (
                    <button
                        role="menuitem"
                        onClick={(e) => {
                            openListPicker(e);
                            close();
                        }}
                        className={MENU_ITEM}
                    >
                        <ListPlus className="h-4 w-4 text-ink-faint" />
                        {list ? "À une autre liste…" : "À une liste…"}
                    </button>
                )}
            </FloatingMenu>
        </div>
    );

    // An external invitation has no establishment to put in a list.
    if (!establishmentId) return menu();
    return (
        <AddToListPicker establishmentIds={[establishmentId]} onAdded={setList}>
            {({ onClick }) => menu(onClick)}
        </AddToListPicker>
    );
}


/** The dashed « + Tag » / « + Note » pill: one look for adding something to a row. */
export const ADD_PILL =
    "inline-flex items-center gap-1 rounded-full border border-dashed border-border-strong px-2 py-0.5 font-sans text-xs text-ink-faint transition-colors hover:border-ink hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-40";

/**
 * The rep's free note, edited where it is shown: its text on its own line when set, a « + Note »
 * pill (the same as « + Tag ») when not. Saves on blur or ⌘/Ctrl+Enter, Escape drops the edit.
 */
export function MemoField({ memo, onSave, disabled }: { memo: string | null; onSave: (text: string) => Promise<void>; disabled?: boolean }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(memo || "");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!editing) setDraft(memo || "");
    }, [memo, editing]);

    const commit = async () => {
        const text = draft.trim();
        if (text === (memo || "")) {
            setEditing(false);
            return;
        }
        setSaving(true);
        try {
            await onSave(text);
            setEditing(false);
        } catch {
            /* stays open, so the text isn't lost; the caller already said why */
        } finally {
            setSaving(false);
        }
    };

    if (editing) {
        return (
            <textarea
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value.slice(0, 1000))}
                onBlur={commit}
                onKeyDown={(e) => {
                    if (e.key === "Escape") {
                        setDraft(memo || "");
                        setEditing(false);
                    } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) commit();
                }}
                onClick={(e) => e.stopPropagation()}
                disabled={saving}
                rows={2}
                placeholder="Votre note… (⌘/Ctrl+Entrée pour enregistrer)"
                className="basis-full resize-none rounded-lg border-0 bg-surface-sunken px-2.5 py-1.5 font-sans text-xs text-ink placeholder:text-ink-faint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            />
        );
    }

    const open = (e: React.MouseEvent) => {
        e.stopPropagation();
        setEditing(true);
    };

    // Empty: the same dashed pill as « + Tag », side by side.
    if (!memo) {
        return (
            <button type="button" disabled={disabled} onClick={open} className={ADD_PILL}>
                <Plus className="h-3 w-3" />
                Note
            </button>
        );
    }

    // Written: on its own line, under the pills.
    return (
        <button
            type="button"
            disabled={disabled}
            onClick={open}
            title="Modifier la note"
            className="group/memo flex basis-full items-start gap-1.5 rounded-lg bg-surface-sunken/60 px-2.5 py-1.5 text-left font-sans text-xs transition-colors hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-40"
        >
            <StickyNote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-faint" />
            <span className="line-clamp-2 whitespace-pre-line text-ink-muted group-hover/memo:text-ink">{memo}</span>
        </button>
    );
}

export function SectionTitle({ children }: { children: ReactNode }) {
    return <p className="mb-2 font-sans text-xs font-semibold uppercase tracking-wide text-ink-faint">{children}</p>;
}

/** A round icon-only button: the row's secondary actions, so its one primary action stands out. */
export const ICON_BUTTON =
    "inline-flex h-8 w-8 items-center justify-center rounded-full border border-border text-ink-muted transition-colors hover:border-border-strong hover:bg-surface-sunken hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-40";

/**
 * "À relancer" with its date presets in a small menu; closes on pick, outside click or Escape.
 * `iconOnly` makes it a round bell (the row) — filled once a follow-up is set.
 */
export function FollowUpMenu({
    onPick, label = "À relancer", disabled, iconOnly, active,
}: { onPick: (iso: string) => void; label?: string; disabled?: boolean; iconOnly?: boolean; active?: boolean }) {
    const [open, setOpen] = useState(false);
    const trigger = useRef<HTMLButtonElement>(null);
    const close = useCallback(() => setOpen(false), []);

    return (
        <div onClick={(e) => e.stopPropagation()}>
            <button
                ref={trigger}
                type="button"
                disabled={disabled}
                onClick={() => setOpen((o) => !o)}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={label}
                title={label}
                className={
                    iconOnly
                        ? `${ICON_BUTTON} ${active ? "!border-accent/30 !bg-accent-subtle !text-accent" : ""}`
                        : "inline-flex h-8 items-center gap-1.5 rounded-full border border-border px-3 font-sans text-sm font-medium text-ink transition-colors hover:border-border-strong hover:bg-surface-sunken focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent disabled:opacity-40"
                }
            >
                <BellRing className="h-4 w-4" />
                {!iconOnly && label}
            </button>
            <FloatingMenu anchor={trigger} open={open} onClose={close}>
                {FOLLOW_UP_PRESETS.map((p) => (
                    <button
                        key={p.days}
                        role="menuitem"
                        onClick={() => {
                            close();
                            onPick(followUpDate(p.days));
                        }}
                        className={MENU_ITEM}
                    >
                        {p.label}
                    </button>
                ))}
            </FloatingMenu>
        </div>
    );
}

/**
 * The prospect's CRM tags, on every row: each tag a pill (× removes it), then « + Tag », which
 * opens the rep's tags with a field that searches them or creates a new one. On a prospect not in
 * the CRM yet, the first tag creates its CRM card — the tags live there.
 */
export function TagMenu({
    tags, allTags, inCrm, onChange, disabled,
}: { tags: string[]; allTags: string[]; inCrm: boolean; onChange: (tags: string[]) => void; disabled?: boolean }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const trigger = useRef<HTMLButtonElement>(null);
    const close = useCallback(() => {
        setOpen(false);
        setQuery("");
    }, []);

    const toggle = (tag: string) => onChange(tags.includes(tag) ? tags.filter((t) => t !== tag) : [...tags, tag]);
    const typed = query.trim();
    const options = Array.from(new Set([...allTags, ...tags]))
        .filter((t) => t.toLowerCase().includes(typed.toLowerCase()))
        .sort((a, b) => a.localeCompare(b));
    const canCreate = !!typed && !options.some((t) => t.toLowerCase() === typed.toLowerCase());

    return (
        <div className="flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
            {tags.map((tag) => (
                <span key={tag} className="group inline-flex items-center gap-1 rounded-full border border-border bg-surface-sunken py-0.5 pl-2 pr-1 font-sans text-xs text-ink-muted">
                    {tag}
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={() => toggle(tag)}
                        aria-label={`Retirer le tag ${tag}`}
                        className="rounded-full p-0.5 text-ink-faint transition-colors hover:bg-surface hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                        <X className="h-3 w-3" />
                    </button>
                </span>
            ))}
            <button
                ref={trigger}
                type="button"
                disabled={disabled}
                onClick={() => (open ? close() : setOpen(true))}
                aria-haspopup="menu"
                aria-expanded={open}
                className={ADD_PILL}
            >
                <Plus className="h-3 w-3" />
                Tag
            </button>
            <FloatingMenu anchor={trigger} open={open} onClose={close} align="left" width={240}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (canCreate) onChange([...tags, typed]);
                        else if (options.length === 1) toggle(options[0]);
                        setQuery("");
                    }}
                >
                    <input
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Chercher ou créer un tag…"
                        maxLength={100}
                        className="h-9 w-full rounded-lg border border-border bg-surface px-3 font-sans text-sm text-ink placeholder:text-ink-faint focus:border-border-strong focus:outline-none"
                    />
                </form>
                <div className="mt-1 max-h-56 overflow-y-auto">
                    {options.map((tag) => {
                        const on = tags.includes(tag);
                        return (
                            <button key={tag} role="menuitemcheckbox" aria-checked={on} onClick={() => toggle(tag)} className={MENU_ITEM}>
                                <span
                                    className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border ${
                                        on ? "border-ink bg-ink text-ink-inverse" : "border-border-strong"
                                    }`}
                                >
                                    {on && <Check className="h-3 w-3" />}
                                </span>
                                <span className="truncate">{tag}</span>
                            </button>
                        );
                    })}
                    {canCreate && (
                        <button
                            role="menuitem"
                            onClick={() => {
                                onChange([...tags, typed]);
                                setQuery("");
                            }}
                            className={MENU_ITEM}
                        >
                            <Plus className="h-4 w-4 text-ink-faint" />
                            <span className="truncate">
                                Créer « <span className="font-medium">{typed}</span> »
                            </span>
                        </button>
                    )}
                    {!options.length && !canCreate && <p className="px-3 py-2 font-sans text-xs text-ink-faint">Aucun tag pour l'instant.</p>}
                </div>
                {!inCrm && (
                    <p className="mt-1 border-t border-border px-3 pb-1 pt-2 font-sans text-xs text-ink-faint">
                        Le premier tag ajoute ce prospect à votre CRM.
                    </p>
                )}
            </FloatingMenu>
        </div>
    );
}
